import { useMemo, useState } from 'react';
import { Box, Typography, Stack, Chip, IconButton, Collapse, Tooltip, Table, TableBody, TableRow, TableCell } from '@mui/material';
import Iconify from '../../../../components/iconify';
import { extractValues, descriptiveStats, computeAQI, sensorKeyToPollutant } from '../statistics';
import { useTimelineControls } from '../TimelineContext';

const CATEGORY_TOOLTIPS = {
  Good: '0\u201350: Air quality is satisfactory, and air pollution poses little or no risk.',
  Moderate: '51\u2013100: Air quality is acceptable; however, some pollutants may pose a moderate health concern for a very small number of people.',
  'Unhealthy (Sensitive)': '101\u2013150: Members of sensitive groups may experience health effects. The general public is less likely to be affected.',
  Unhealthy: '151\u2013200: Some members of the general public may experience health effects; members of sensitive groups may experience more serious health effects.',
  'Very Unhealthy': '201\u2013300: Health alert \u2014 the risk of health effects is increased for everyone.',
  Hazardous: '301\u2013500: Health warning of emergency conditions \u2014 everyone is more likely to be affected.',
};

function AQIGauge({ aqi, category, color }) {
  const pct = Math.min(aqi / 500, 1);
  const tooltip = CATEGORY_TOOLTIPS[category] || category;
  return (
    <Box sx={{ textAlign: 'center', py: 1 }}>
      <Box sx={{ position: 'relative', width: 120, height: 120, mx: 'auto' }}>
        <svg viewBox="0 0 120 120" width="120" height="120">
          {/* Background arc */}
          <circle cx="60" cy="60" r="50" fill="none" stroke="#eee" strokeWidth="12"
            strokeDasharray="235.6" strokeDashoffset="0"
            transform="rotate(135 60 60)" strokeLinecap="round" />
          {/* Value arc */}
          <circle cx="60" cy="60" r="50" fill="none" stroke={color} strokeWidth="12"
            strokeDasharray={`${pct * 235.6} 235.6`} strokeDashoffset="0"
            transform="rotate(135 60 60)" strokeLinecap="round" />
        </svg>
        <Tooltip title={tooltip} arrow placement="top">
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'default' }}>
            <Typography variant="h4" fontWeight="bold" sx={{ color, lineHeight: 1 }}>{aqi}</Typography>
            <Typography variant="caption" color="text.secondary">AQI</Typography>
          </Box>
        </Tooltip>
      </Box>
      <Tooltip title={tooltip} arrow>
        <Chip label={category} size="small" sx={{ mt: 0.5, bgcolor: color, color: '#fff', fontWeight: 'bold' }} />
      </Tooltip>
    </Box>
  );
}

const SENSOR_MAPPINGS = [
  { sensor: 'PM2.5', pollutant: 'pm2.5' },
  { sensor: 'PM10', pollutant: 'pm10' },
  { sensor: 'CO2 (ppm)', pollutant: 'co2' },
];

function MethodologyPanel({ open }) {
  return (
    <Collapse in={open} timeout="auto" unmountOnExit>
      <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="caption" fontWeight="bold" gutterBottom sx={{ display: 'block' }}>
          EPA AQI Methodology
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          AQI calculated using the EPA Air Quality Index methodology (40 CFR Part 58, Appendix G).
        </Typography>

        <Typography variant="caption" fontWeight="bold" gutterBottom sx={{ display: 'block' }}>
          Sensor Mapping
        </Typography>
        <Table size="small" sx={{ mb: 1, '& td': { py: 0.25, px: 0.5, border: 0, fontSize: '0.7rem' } }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Sensor</TableCell>
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Pollutant</TableCell>
            </TableRow>
            {SENSOR_MAPPINGS.map(({ sensor, pollutant }) => (
              <TableRow key={pollutant}>
                <TableCell>{sensor}</TableCell>
                <TableCell sx={{ color: 'text.disabled' }}>&rarr;</TableCell>
                <TableCell>{pollutant}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Typography variant="caption" fontWeight="bold" gutterBottom sx={{ display: 'block' }}>
          AQI Scale
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          AQI is based on the highest sub-index. Scale: 0&#8211;50 Good, 51&#8211;100 Moderate,
          101&#8211;150 Unhealthy for Sensitive Groups, 151&#8211;200 Unhealthy,
          201&#8211;300 Very Unhealthy, 301&#8211;500 Hazardous.
        </Typography>
      </Box>
    </Collapse>
  );
}

export default function AQIWidget({ recordings = [], widgetConfig }) {
  const { window: timeWindow } = useTimelineControls();
  const [showInfo, setShowInfo] = useState(false);
  const recId = widgetConfig?.recId || '';
  const sensorKey = widgetConfig?.sensorKey || '';

  const rec = recordings.find((r) => r.id === recId);

  // Only show AQI-compatible sensors
  const aqiSensorKeys = useMemo(() => {
    if (!rec?.data) return [];
    return Object.keys(rec.data).filter((k) => sensorKeyToPollutant(k) !== null);
  }, [rec]);

  const result = useMemo(() => {
    if (!rec || !sensorKey) return null;
    const pollutant = sensorKeyToPollutant(sensorKey);
    if (!pollutant) return null;

    const values = extractValues(rec, sensorKey, timeWindow?.start, timeWindow?.end);
    if (values.length === 0) return null;
    const stats = descriptiveStats(values);
    if (!stats) return null;

    const currentAQI = computeAQI(pollutant, stats.mean);
    const maxAQI = computeAQI(pollutant, stats.max);
    const minAQI = computeAQI(pollutant, stats.min);

    return { pollutant, current: currentAQI, max: maxAQI, min: minAQI, mean: stats.mean, count: stats.count };
  }, [rec, sensorKey, timeWindow]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 0.5, pt: 0.25, flexShrink: 0 }}>
        <Tooltip title={showInfo ? 'Hide methodology' : 'Show AQI methodology'} arrow>
          <IconButton
            size="small"
            onClick={() => setShowInfo((prev) => !prev)}
            aria-label="Toggle AQI methodology info"
            sx={{ opacity: showInfo ? 1 : 0.5, '&:hover': { opacity: 1 }, transition: 'opacity 0.15s' }}
          >
            <Iconify icon="eva:info-fill" width={16} />
          </IconButton>
        </Tooltip>
      </Stack>

      <MethodologyPanel open={showInfo} />

      {result ? (
        <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {timeWindow && <Chip label="Windowed" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
          <AQIGauge aqi={result.current.aqi} category={result.current.category} color={result.current.color} />
          <Typography variant="caption" color="text.secondary">Mean: {result.mean.toFixed(1)} ({result.pollutant.toUpperCase()})</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <Stack alignItems="center">
              <Tooltip title={CATEGORY_TOOLTIPS[result.min.category] || result.min.category} arrow>
                <Typography variant="caption" fontWeight="bold" sx={{ color: result.min.color, cursor: 'default' }}>{result.min.aqi}</Typography>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">Min AQI</Typography>
            </Stack>
            <Stack alignItems="center">
              <Tooltip title={CATEGORY_TOOLTIPS[result.max.category] || result.max.category} arrow>
                <Typography variant="caption" fontWeight="bold" sx={{ color: result.max.color, cursor: 'default' }}>{result.max.aqi}</Typography>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">Max AQI</Typography>
            </Stack>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>n={result.count}</Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {aqiSensorKeys.length === 0 && rec ? 'No AQI-compatible sensors (PM2.5, PM10, CO2)' : 'Select a recording and pollutant'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
